// UI strings for Ääntämisvalmentaja's sound guides (docs/design/AANTAMISVALMENTAJA.md "G content"):
// per sound how to place the tongue, lips and voice, the Finnish sound to start from and the step
// from it, a test you can feel or see, the typical Finnish error and how it sounds, what else counts
// and how much of Peterson's audio there is; and each ladder step's one-line instruction, keyed by
// the step ids of engine/src/rules/coach-sounds.ts (a test checks every id has a line in both languages).
// HV words are in «…» (the screen sets them in the HV face); they are Peterson's words from the
// ladders or dictionary words (the error pairs: jēdrot/iēdrot, zān/sān, dēmas/tēmas, Ghīs/gīs,
// qēlītsos/kēlītsos, zȳrho/zȳro, pykagon/pikagon), their meanings from the app's Finnish glosses.
// Style: docs/FINNISH-STYLE.md (sinä, no jargon, the grammar terms of Kielioppi §1). en has the same shape.
import { defineArea } from '../area';

export const coachGuideFi = {
  /** The ladder's steps by kind (LadderKind). */
  kinds: {
    hear: 'Kuuntele',
    feel: 'Etsi äänne',
    sustain: 'Äänne yksinään',
    'between-vowels': 'Vokaalien välissä',
    pair: 'Parit',
    initial: 'Sanan alussa',
    final: 'Sanan lopussa',
    word: 'Sanassa',
    line: 'Lauseessa',
  },
  /** What a practice word was chosen for: the place of the sound, b/d/g with its place, or a stress pattern. */
  groups: {
    initial: 'sanan alussa',
    'between-vowels': 'vokaalien välissä',
    final: 'sanan lopussa',
    medial: 'konsonantin vieressä',
    'first-of-two': 'paino ensimmäisellä kahdesta tavusta',
    penult: 'paino toiseksi viimeisellä tavulla',
    antepenult: 'paino kolmanneksi viimeisellä tavulla',
    'final-stress': 'käsky: paino viimeisellä tavulla',
  },
  /** Under a drill prompt (s → z, aza · eze …). */
  drill: 'Harjoitustavuja, ei sanoja',
  /** Under a word played from Peterson's line: never presented as a recording of the word alone. */
  inLine: 'kohta Petersonin lauseesta',
  /** How the listening step is passed (COACH_PASS hear: listens, once). */
  pass: (hits: number, days: number) => (days > 1 ? `${hits} onnistunutta toistoa ${days} eri päivänä` : `${hits} onnistunutta toistoa`),
  /** How a speaking step is passed (engine pedagogy/coach.ts MASTERY: Axel 2026-10-11). */
  mastery: (pct: number, days: number, perDay: number) =>
    `Vähintään ${pct} % toistoista onnistuu ${days} eri päivänä. Päivä lasketaan, kun valmentaja on arvioinut siltä vähintään ${perDay} toistoa.`,
  /** A passed step opened again by a weak day (MASTERY.reopenBelow). */
  reopened: (pct: number) => `Avattu uudelleen: alle ${pct} % toistoista onnistui.`,
  /** How much of Peterson's audio models the sound (CoachSound.modelAudio). */
  modelAudio: {
    good: '',
    thin: 'Petersonin äänitteissä tätä äännettä on vain vähän.',
    none: 'Petersonin äänitteissä tätä äännettä ei kuule.',
  },
  sounds: {
    z: {
      name: 'z',
      title: 'Soinnillinen s',
      place:
        'Kielen kärki on hammasvallin takana niin kuin s:ssä, ja ilma kulkee kapeasta urasta. Leuka on melkein kiinni. Äänihuulet värisevät koko äänteen ajan.',
      start: 'Aloita pitkästä suomen s:stä. Kun se soi tasaisesti, hymise sen alle liikuttamatta kieltä: s muuttuu z:ksi.',
      test: 'Pane kämmenet korvillesi tai sormet kurkunpäälle. z surisee, s ei.',
      error: 'Suomalaisen z jää helposti s:ksi, jolloin «zān» ’eilen’ kuulostaa sanalta «sān» ’käytän’. Sanan lopussa surina katkeaa helpoimmin.',
      steps: {
        'z-hear': 'Kuuntele z vokaalien välissä, ensin hitaasti ja sitten normaalisti.',
        'z-feel': 'Sano pitkä s ja anna äänihuulten alkaa väristä kesken äänteen. Kieli pysyy paikallaan.',
        'z-sustain': 'Pidä z-äännettä kolme sekuntia niin, ettei surina katkea.',
        'z-between-vowels': 'Sano z jokaisen vokaalin välissä. Surina jatkuu vokaalista toiseen.',
        'z-initial': 'Aloita surina heti: sana alkaa z:llä, ei s:llä.',
        'z-final': 'Pidä surina päällä loppuun asti, vaikka ääni hiljenee.',
        'z-word': 'Sanassa on kaksi z-äännettä, alussa ja vokaalien välissä. Molemmat surisevat.',
        'z-line': 'Sano lause Petersonin kanssa, ensin hitaasti. Jokainen z surisee.',
      },
    },
    j: {
      name: 'j',
      title: 'Sulku ja lyhyt sihinä',
      place:
        'j on kaksi liikettä yhdessä. Kielen kärki ja terä sulkevat ilman hammasvallia vasten niin kuin d:ssä, ja sulku aukeaa suoraan lyhyeen surisevaan š-sihinään. Huulet pyöristyvät hieman, ja äänihuulet värisevät koko ajan.',
      start:
        'Äännä niin kuin englannin sanoissa ”James” ja ”jump”: lyhyt sulku, pamahdus ja surina heti. Toinen tapa: sano suomen d ja heti perään š niin kuin sanassa šakki, mutta surisevana. Nopeuta, kunnes kuulet yhden äänteen: dž.',
      test: 'j-äännettä ei voi venyttää. Pidä kämmen suun edessä: kun sulku aukeaa, tunnet pienen puhalluksen.',
      error:
        'Suomen j on liukuma, jossa mikään ei sulkeudu. Silloin «jēdrot» ’nouse!’ kuulostaa sanalta «iēdrot» ’juo!’. Jos sihinä venyy pitkäksi, j kuulostaa ž:ltä.',
      variants: 'Sanakirjan IPA-merkinnöissä näkyvä vanhempi ääntämys [ɟ] käy myös (Peterson).',
      steps: {
        'j-hear': 'Kuuntele sanan alku: ensin sulku, sitten lyhyt sihinä. Suomen j:tä siinä ei ole.',
        'j-feel-en': 'Äännä niin kuin englannin sanoissa ”James” ja ”jump”: lyhyt sulku, pamahdus ja surina heti. Pidä tauko ennen jokaista sanaa.',
        'j-feel': 'Sano d, ja anna sulun aueta suoraan lyhyeen surisevaan š:hön: d, dž.',
        'j-sustain':
          'Sano tavut yksi kerrallaan ja pidä tauko ennen jokaista. Kieli on kiinni jo tauon aikana, ja sulku aukeaa napsahtaen. Sihinä on lyhyt, eikä se ala vähitellen.',
        'j-between-vowels': 'Sano ensin vokaali ja heti perään tavu. Kieli sulkeutuu vokaalin lopussa, ja surina jatkuu sulun läpi.',
        'j-initial': 'Sana alkaa sululla: kieli on hammasvallissa kiinni jo ennen kuin ääni alkaa.',
        'j-initial-2': 'Sama sanan alussa: sulku ensin, sitten lyhyt sihinä ja heti ääni. Ei pitkää ts-sihinää eikä pehmeää ž:tä.',
        'j-word': 'j on vokaalien välissä. Sulje ja avaa nopeasti, surina päällä.',
        'j-line': 'Lauseessa on neljä j-äännettä. Sano se Petersonin kanssa, ensin hitaasti.',
      },
    },
    v: {
      name: 'v',
      title: 'Alahuuli hampaita vasten',
      place: 'Alahuuli koskettaa kevyesti yläetuhampaita, ja ilma hankaa niiden välistä. Äänihuulet värisevät.',
      start: 'Aloita f:stä ja anna äänihuulten väristä: f muuttuu v:ksi. Suomen v on pehmeämpi, koska huuli vain lähestyy hampaita.',
      test: 'Peilistä näet, että alahuuli on yläetuhampaiden alla. Sormilla kurkunpäästä tunnet surinan.',
      error: 'Suomen pehmeä v ei ole virhe vaan lähellä: Peterson pitää sitäkin mahdollisena vanhassa valyriassa. Tavoite on kuuluva hankaus.',
      variants: 'Vanhemmassa kielessä v oli usein [w] kuin englannin w (Peterson). Sekin käy.',
      steps: {
        'v-hear': 'Kuuntele v vokaalien välissä, ensin hitaasti ja sitten normaalisti.',
        'v-feel': 'Sano f ja lisää ääni. Huuli pysyy hampailla.',
        'v-sustain': 'Pidä v-äännettä kolme sekuntia: hankaus ja surina yhtä aikaa.',
        'v-between-vowels': 'Sano v jokaisen vokaalin välissä. Huuli käy hampailla joka kerta.',
        'v-initial': 'Vie huuli hampaille jo ennen ääntä.',
        'v-word': 'm:n jälkeen v jää helposti pehmeäksi. Vie huuli hampaille.',
        'v-line': 'Sano lause Petersonin kanssa. Kuuntele jokaista v-äännettä.',
      },
    },
    bdg: {
      name: 'b, d, g',
      title: 'Surina sulun aikana',
      place:
        'Huulet (b), kielen kärki hammasvallissa (d) tai kielen takaosa pehmeässä suulaessa (g) sulkevat ilman, ja äänihuulet värisevät jo sulun aikana. Valyrian p, t ja k ovat henkäyksettömiä niin kuin suomessa, joten vain surina erottaa niistä b:n, d:n ja g:n.',
      start:
        'Aloita nenä-äänteestä: sano m ennen b:tä ja n ennen d:tä, ja lyhennä nenä-äännettä, kunnes jäljelle jää pelkkä surina ennen sulun aukeamista. Ennen g:tä sano n niin kuin sanassa kenkä.',
      test: 'Pidä sormia kurkunpäällä. b:ssä, d:ssä ja g:ssä surina alkaa jo ennen kuin huulet tai kieli aukeavat. Soinnittomissa p:ssä, t:ssä ja k:ssa sulku on hiljainen.',
      error: 'Soinnittomaksi jäävä d kuulostaa t:ltä: «dēmas» ’istuu’ muuttuu sanaksi «tēmas» ’pistää’.',
      steps: {
        'bdg-hear': 'Kuuntele b vokaalien välissä, ensin hitaasti ja sitten normaalisti. Surina jatkuu sulun läpi.',
        'bdg-feel': 'Sano tavut ja lyhennä nenä-äännettä, kunnes jäljelle jää surina.',
        'bdg-sustain': 'Pidä sulkua ja surinaa hetki ennen kuin avaat.',
        'bdg-between-vowels': 'Sano b, d ja g kahden a:n välissä. Surina ei katkea sulun aikana.',
        'bdg-initial': 'd surisee jo ennen kuin kieli irtoaa.',
        'bdg-word': 'b on vokaalien välissä. Älä anna sen muuttua p:ksi.',
        'bdg-line': 'Lauseessa on yksi g ja kaksi d:tä. Sano se Petersonin kanssa.',
      },
    },
    gh: {
      name: 'gh',
      title: 'Soinnillinen kurkkuäänne',
      place: 'Kielen takaosa nousee lähelle pehmeää suulakea tai kitakielekettä mutta ei kosketa sitä. Ilma hankaa raosta, ja äänihuulet värisevät.',
      start:
        'Aloita suomen h:sta sanassa kahvi: se hankaa jo oikeassa paikassa. Anna äänihuulten väristä, niin saat gh:n. Toinen tie: sano g kahden a:n välissä ja löysää sulkua, kunnes ilma ei enää pysähdy.',
      test: 'gh-äännettä voi venyttää, g:tä ei. Jos ilma pysähtyy hetkeksi, sanoit g:n.',
      error:
        'Suomalainen sanoo helposti g:n tai k:n, jolloin ilma pysähtyy ja kuuluu pieni poksahdus: «Ghīs» (kaupunki) kuulostaa sanalta «gīs» ’sielu’. Pelkkä h taas on liian heikko.',
      variants:
        'Petersonin mukaan gh voi olla pehmeän suulaen tai kitakielekkeen kohdalla. Hänen omissa äänitteissään se on usein osittain soinniton, joten soinniton gh on lähellä eikä virhe.',
      model: 'Petersonin äänitteissä gh on lähes aina saman sanan muodoissa, «morghūlis», «morghon».',
      steps: {
        'gh-hear': 'Kuuntele gh sanan keskellä, ensin hitaasti ja sitten normaalisti. Ilma hankaa eikä pysähdy.',
        'gh-feel': 'Sano kahvi ja venytä h:ta. Anna sitten äänihuulten väristä.',
        'gh-sustain': 'Pidä gh-äännettä kolme sekuntia. Ilma virtaa koko ajan.',
        'gh-between-vowels': 'Sano gh jokaisen vokaalin välissä ilman sulkua.',
        'gh-word': 'gh on vokaalien välissä. Ilma virtaa koko ajan.',
        'gh-word-2': 'r:n jälkeen gh muuttuu helposti g:ksi. Pidä rako auki.',
        'gh-line': 'Sano lause Petersonin kanssa, ensin hitaasti.',
      },
    },
    q: {
      name: 'q',
      title: 'Kuin k mutta paljon taempana',
      place: 'Kielen takaosa sulkee ilman kitakielekettä vasten, selvästi k:ta taempana. Viereiset vokaalit kuulostavat tummemmilta.',
      start: 'Sano suomen k niin kuin sanassa kuu ja siirrä sulkua taaksepäin, kunnes kieli koskettaa kitakielekettä.',
      test: 'Sano vuorotellen ka ja qa. q:ssa kosketus tuntuu syvemmällä, ja a kuulostaa tummemmalta.',
      error:
        'Ero kuuluu sanoissa «qēlītsos» ’kynttilä’ ja «kēlītsos» ’kissanpentu’. Peterson sanoo itsekin q:n useimmiten k:na, joten k on lähellä eikä koskaan virhe.',
      variants: 'k käy: Peterson sanoo q:n omissa äänitteissään k:na.',
      model: 'Petersonin äänitteissä q kuulostaa k:lta. Kuuntele niistä sanan muut äänteet, ja katso q:n paikka kuvasta.',
      steps: {
        'q-hear': 'Kuuntele sanan rytmi ja muut äänteet. Peterson sanoo q:n tässä k:na.',
        'q-feel': 'Sano k ja siirrä sulkua taaksepäin kitakielekkeen kohdalle.',
        'q-sustain': 'q-äännettä ei voi venyttää, joten toista tavua. Sulku on joka kerta yhtä takana.',
        'q-between-vowels': 'Sano q jokaisen vokaalin välissä. i:n ja e:n vieressä q pyrkii eteen: pidä se takana.',
        'q-initial': 'Sana alkaa sululla kitakielekettä vasten.',
        'q-word': 'q on sanan alussa i:n edellä. Pidä sulku takana.',
        'q-line': 'Sano lause Petersonin kanssa. Hän sanoo q:n k:na, sinä sanot sen takaa.',
      },
    },
    rh: {
      name: 'rh',
      title: 'Soinniton r',
      place: 'Kielen kärki tärisee hammasvallia vasten niin kuin r:ssä, mutta äänihuulet eivät värise. Ääni alkaa vasta vokaalissa.',
      start: 'Kuiskaa suomen r ja sano sen perään vokaali täydellä äänellä.',
      test: 'Pidä sormia kurkunpäällä: rh:n aikana ei surise, vokaalissa surisee. Kämmenellä suun edessä tunnet ilmavirran.',
      error: 'Soinnillinen r on rh:n tavallisin virhe: «zȳrho» ’hänen’ kuulostaa silloin sanalta «zȳro» ’puolison’.',
      model: 'Petersonin äänitteissä rh on vain muutamassa sanassa. Kuva näyttää äänteen, ja äänitteestä kuulet sanan rytmin.',
      steps: {
        'rh-hear': 'Kuuntele sanan alku, ensin hitaasti ja sitten normaalisti. rh on täry ilman ääntä, ja ääni alkaa vokaalissa.',
        'rh-feel': 'Kuiskaa r ja sano vokaali sen perään ääneen.',
        'rh-sustain': 'Pidä soinnitonta täryä kolme sekuntia.',
        'rh-between-vowels': 'Sano rh jokaisen vokaalin välissä: vokaali soi, rh ei.',
        'rh-initial': 'Sana alkaa soinnittomalla täryllä.',
        'rh-line': 'Sano lause Petersonin kanssa. rh on heti alussa.',
      },
    },
    lj: {
      name: 'lj',
      title: 'l ja j yhtä aikaa',
      place: 'Kielen keskiosa painuu kovaa suulakea vasten, ja ilma kulkee kielen sivuilta. Kielen kärki on alhaalla alahampaiden takana.',
      start: 'Sano kalja ja sulauta l ja j yhdeksi äänteeksi: pidä kielen kärki alhaalla ja paina keskiosa kovaa suulakea vasten.',
      test: 'Peilistä näet, ettei kielen kärki nouse. Äännettä voi venyttää.',
      error: 'Suomalainen sanoo helposti ensin l:n ja sitten j:n. Silloin kuuluu kaksi liikettä yhden sijasta.',
      model: 'Petersonin äänitteissä lj on vain kahdessa sanassa, molemmat lauseen keskellä. Kuva näyttää äänteen.',
      steps: {
        'lj-hear': 'Kuuntele: lj on yksi äänne.',
        'lj-feel': 'Sano kalja ja sulauta l ja j yhteen.',
        'lj-sustain': 'Pidä lj-äännettä kolme sekuntia kärki alhaalla.',
        'lj-between-vowels': 'Sano lj jokaisen vokaalin välissä yhtenä äänteenä.',
        'lj-word': 'lj tulee pitkän ū:n jälkeen yhtenä liikkeenä.',
        'lj-line': 'Sano lause Petersonin kanssa, ensin hitaasti. lj on sanassa «morghūljosy».',
      },
    },
    ny: {
      name: 'ñ',
      title: 'n ja j yhtä aikaa',
      place: 'Kielen keskiosa sulkee ilman kovaa suulakea vasten, ja ilma kulkee nenän kautta. Kielen kärki on alhaalla alahampaiden takana.',
      start: 'Sano Sonja ja sulauta n ja j yhdeksi äänteeksi.',
      test: 'Purista sieraimet kiinni: ñ katkeaa, koska ilma kulkee nenän kautta. Peilistä näet, ettei kielen kärki nouse.',
      error: 'Suomalainen sanoo helposti n:n ja perään j:n tai pelkän n:n. Petersonin ñ on pitkä ja yhtenäinen.',
      steps: {
        'ny-hear': 'Kuuntele sanan alkua: yksi äänne, ei n ja j.',
        'ny-feel': 'Sano Sonja ja sulauta n ja j yhteen.',
        'ny-sustain': 'Pidä ñ-äännettä kolme sekuntia kärki alhaalla.',
        'ny-between-vowels': 'Sano ñ jokaisen vokaalin välissä yhtenä äänteenä.',
        'ny-initial': 'Sana alkaa ñ:llä. Älä aloita n:llä.',
        'ny-word': 'ñ on vokaalien välissä yhtenä äänteenä, ei n ja j.',
        'ny-line': 'Sano lause Petersonin kanssa. ñ on sanassa «ōñoso».',
      },
    },
    y: {
      name: 'y',
      title: 'Suomen y',
      place: 'Kieli on kuin i:ssä korkealla ja edessä, mutta huulet ovat pyöreinä kuin u:ssa.',
      start: 'Sinulla on jo oikea y. Sano pitkä i ja pyöristä huulet liikuttamatta kieltä: i muuttuu y:ksi.',
      test: 'Katso peiliin: y:ssä huulet ovat pyöreät ja työntyvät eteen, i:ssä ne ovat leveällä.',
      error:
        'Sarjojen näyttelijöistä moni sanoo y:n i:nä, ja Petersoninkin y kuulostaa äänitteissä usein i:ltä. Sano silti oma suomen y:si. Ero kuuluu sanoissa «pykagon» ’sylkeä’ ja «pikagon» ’seurata’.',
      variants: 'Petersonin mukaan nykyiset puhujat sanovat y:n usein ilman huulten pyöristystä, joten i on lähellä eikä virhe. Tavoite on silti y.',
      model: 'Petersonin y ja i kuulostavat hänen äänitteissään lähes samalta. Mallina on oma suomen y:si.',
      steps: {
        'y-hear': 'Kuuntele sana Petersonilta, ja sano y itse pyöreillä huulilla.',
        'y-feel': 'Sano i ja pyöristä huulet. Kieli pysyy paikallaan.',
        'y-sustain': 'Pidä y-äännettä kolme sekuntia huulet pyöreinä.',
        'y-pair': 'Sano tavuparit vuorotellen. Vain huulet liikkuvat.',
        'y-initial': 'Huulet ovat pyöreinä heti alusta.',
        'y-final': 'Sana päättyy y:hyn. Älä päästä huulia leveiksi lopussa.',
        'y-word': 'y on viimeisessä tavussa. Pidä huulet pyöreinä.',
        'y-line': 'Lauseessa on kolme y-äännettä. Sano se Petersonin kanssa omalla y:lläsi.',
      },
    },
    r: {
      name: 'r',
      title: 'Täryäänne kuten suomessa',
      place: 'Kielen kärki värähtää hammasvallia vasten kaksi tai kolme kertaa, ja äänihuulet värisevät.',
      start: 'Suomen r on jo oikea. Pidä se täryäänteenä myös valyrian sanoissa, äläkä lyhennä sitä yhdeksi napautukseksi.',
      test: 'Pidä r-äännettä: täry jatkuu niin kauan kuin ilmaa riittää.',
      error:
        'Sarjojen näyttelijät sanovat r:n usein yhtenä napautuksena. Wikin mukaan sekin käy varsinkin klusiilin jälkeen, joten napautus on lähellä eikä virhe.',
      variants: 'Yksi napautus käy varsinkin klusiilin jälkeen (wiki).',
      steps: {
        'r-hear': 'Kuuntele r vokaalien välissä.',
        'r-feel': 'Sano pitkä suomen r ja tunne, miten kielen kärki värisee.',
        'r-between-vowels': 'Sano r jokaisen vokaalin välissä. Kaksi tai kolme täryä riittää.',
        'r-initial': 'Sana alkaa r:llä: täry heti alusta.',
        'r-final': 'Sana päättyy r:ään. Anna sen täristä loppuun asti.',
        'r-word': 'r on vokaalien välissä. Lyhytkin täry riittää.',
        'r-line': 'Sano lause Petersonin kanssa. Kummassakin sanassa on r vokaalien välissä.',
      },
    },
    voice: {
      name: 'Paino ja sävel',
      title: 'Missä ääni nousee',
      place: 'Valyriassa paino lasketaan sanan lopusta, ja painollisella tavulla ääni nousee. Suomessa paino on aina ensimmäisellä tavulla.',
      start: 'Napauta pöytää jokaisella tavulla ja painollisella tavulla kovempaa. Anna äänesi nousta samassa kohdassa.',
      test: 'Seuraa sävelkäyrää: sen pitää nousta painollisen tavun kohdalla eikä sanan alussa.',
      error: 'Suomalainen painottaa ensimmäistä tavua: KI·rim·vo·se. Oikein on ki·RIM·vo·se. Käskymuodossa paino on viimeisellä tavulla.',
      model: 'Sävelkäyrä on mittaus eikä arvosana. Se näyttää, missä äänesi nousee, mutta ei ratkaise, oliko paino oikealla tavulla.',
      steps: {
        'voice-hear': 'Kuuntele, miten ääni nousee toisella tavulla.',
        'voice-feel': 'Napauta tavut pöytään, painollinen kovempaa.',
        'voice-sustain': 'Sano pitkä a ja anna äänen nousta ja laskea. Näin näet, kuinka laaja äänialasi on.',
        'voice-initial': 'Kaksitavuisessa sanassa paino on alussa niin kuin suomessa.',
        'voice-final': 'Sana on käsky, joten paino on viimeisellä tavulla.',
        'voice-word': 'Paino on keskimmäisellä tavulla.',
        'voice-word-2': 'Paino on ensimmäisellä tavulla, koska toiseksi viimeinen tavu on kevyt ja sitä edeltävä raskas.',
        'voice-line': 'Sano lause Petersonin kanssa. Äänesi nousee jokaisen sanan painollisella tavulla.',
      },
    },
  },
};

export const coachGuideEn: typeof coachGuideFi = {
  kinds: {
    hear: 'Listen',
    feel: 'Find the sound',
    sustain: 'The sound alone',
    'between-vowels': 'Between vowels',
    pair: 'Pairs',
    initial: 'At the start of a word',
    final: 'At the end of a word',
    word: 'In a word',
    line: 'In a line',
  },
  groups: {
    initial: 'at the start of a word',
    'between-vowels': 'between vowels',
    final: 'at the end of a word',
    medial: 'next to a consonant',
    'first-of-two': 'stress on the first of two syllables',
    penult: 'stress on the second-to-last syllable',
    antepenult: 'stress on the third-to-last syllable',
    'final-stress': 'a command: stress on the last syllable',
  },
  drill: 'Practice syllables, not words',
  inLine: 'a part of Peterson’s line',
  pass: (hits, days) => (days > 1 ? `${hits} good takes on ${days} different days` : `${hits} good takes`),
  mastery: (pct, days, perDay) =>
    `At least ${pct} % of your takes succeed on ${days} different days. A day counts once the coach has judged at least ${perDay} takes.`,
  reopened: (pct) => `Opened again: fewer than ${pct} % of your takes succeeded.`,
  modelAudio: {
    good: '',
    thin: 'Peterson’s recordings have little of this sound.',
    none: 'Peterson’s recordings do not have this sound.',
  },
  sounds: {
    z: {
      name: 'z',
      title: 'A voiced s',
      place:
        'The tip of your tongue sits behind the gum ridge as for s, and the air goes through a narrow groove. Your jaw is almost closed. Your vocal cords buzz the whole time.',
      start: 'Start from a long Finnish s. Once it flows evenly, hum under it without moving your tongue: the s turns into z.',
      test: 'Put your palms over your ears or your fingers on your throat. z buzzes, s does not.',
      error:
        'A Finnish speaker’s z easily turns into s, and then «zān» ‘yesterday’ sounds like «sān» ‘I use’. At the end of a word the buzz is the easiest to lose.',
      steps: {
        'z-hear': 'Listen to z between vowels, slowly first, then at normal speed.',
        'z-feel': 'Say a long s and let your vocal cords start buzzing halfway through. Your tongue stays put.',
        'z-sustain': 'Hold z for three seconds without the buzz breaking.',
        'z-between-vowels': 'Say z between each pair of vowels. The buzz runs on from one vowel to the next.',
        'z-initial': 'Start buzzing at once: the word starts with z, not s.',
        'z-final': 'Keep the buzz on to the end, even as your voice fades.',
        'z-word': 'The word has two z sounds, at the start and between vowels. Both buzz.',
        'z-line': 'Say the line with Peterson, slowly first. Every z buzzes.',
      },
    },
    j: {
      name: 'j',
      title: 'A closure and a short hiss',
      place:
        'j is two movements in one. The tip and blade of your tongue close off the air against the gum ridge as for d, and the closure opens straight into a short buzzing sh-hiss. Your lips round slightly, and your vocal cords buzz the whole time.',
      start:
        'Say it as in English James and jump: a short closure, a pop and the buzz at once. Another way: say a Finnish d and right after it a buzzing sh, as in šakki. Speed up until you hear one sound: dzh, as in English jam.',
      test: 'You cannot hold a j. Hold your palm in front of your mouth: as the closure opens you feel a small puff.',
      error: 'A Finnish j is a glide where nothing closes. Then «jēdrot» ‘rise!’ sounds like «iēdrot» ‘drink!’. If the hiss runs long, j sounds like zh.',
      variants: 'The older pronunciation [ɟ] shown in the dictionary’s IPA also counts (Peterson).',
      steps: {
        'j-hear': 'Listen to the start of the word: first a closure, then a short hiss. There is no Finnish j in it.',
        'j-feel-en': 'Say it as in English James and jump: a short closure, a pop and the buzz at once. Pause before each word.',
        'j-feel': 'Say d and let the closure open straight into a short buzzing sh: d, dzh.',
        'j-sustain':
          'Say the syllables one at a time with a pause before each. Your tongue is already shut during the pause, and the closure opens with a click. The hiss is short and does not fade in.',
        'j-between-vowels':
          'Say the vowel first and the syllable right after it. Your tongue closes at the end of the vowel, and the buzz runs on through the closure.',
        'j-initial': 'The word starts with a closure: your tongue is shut on the gum ridge before the sound begins.',
        'j-initial-2': 'The same at the start of a word: the closure first, then a short hiss and the voice at once. No long ts hiss and no soft zh.',
        'j-word': 'j sits between vowels. Close and open quickly, with the buzz on.',
        'j-line': 'The line has four j sounds. Say it with Peterson, slowly first.',
      },
    },
    v: {
      name: 'v',
      title: 'Lower lip against the teeth',
      place: 'Your lower lip lightly touches your upper front teeth, and the air rubs between them. Your vocal cords buzz.',
      start: 'Start from f and let your vocal cords buzz: f turns into v. A Finnish v is softer because the lip only comes close to the teeth.',
      test: 'In a mirror you see your lower lip under your upper front teeth. Your fingers on your throat feel the buzz.',
      error: 'The soft Finnish v is not an error but close: Peterson calls it plausible for old Valyrian too. Aim for audible friction.',
      variants: 'In the older language v was often [w], as in English w (Peterson). That counts too.',
      steps: {
        'v-hear': 'Listen to v between vowels, slowly first, then at normal speed.',
        'v-feel': 'Say f and add your voice. Your lip stays on your teeth.',
        'v-sustain': 'Hold v for three seconds: friction and buzz together.',
        'v-between-vowels': 'Say v between each pair of vowels. Your lip touches your teeth every time.',
        'v-initial': 'Put your lip on your teeth before the sound starts.',
        'v-word': 'After m, v easily goes soft. Put your lip on your teeth.',
        'v-line': 'Say the line with Peterson. Listen to every v.',
      },
    },
    bdg: {
      name: 'b, d, g',
      title: 'A buzz during the closure',
      place:
        'Your lips (b), the tip of your tongue on the gum ridge (d) or the back of your tongue on the soft palate (g) close off the air, and your vocal cords already buzz during the closure. High Valyrian p, t and k are unaspirated as in Finnish, so only the buzz tells b, d and g apart from them.',
      start:
        'Start from a nasal: say m before b and n before d, and shorten the nasal until only a buzz is left before the closure opens. Before g, say the n as in Finnish kenkä.',
      test: 'Keep your fingers on your throat. In b, d and g the buzz starts before your lips or tongue open; in p, t and k the closure is silent.',
      error: 'A d that loses its voice sounds like t: «dēmas» ‘sits’ turns into «tēmas» ‘pokes’.',
      steps: {
        'bdg-hear': 'Listen to b between vowels, slowly first, then at normal speed. The buzz runs on through the closure.',
        'bdg-feel': 'Say the syllables and shorten the nasal until only the buzz is left.',
        'bdg-sustain': 'Hold the closure and the buzz for a moment before you open.',
        'bdg-between-vowels': 'Say b, d and g between two a sounds. The buzz does not break during the closure.',
        'bdg-initial': 'The d buzzes before your tongue lets go.',
        'bdg-word': 'The b sits between vowels. Do not let it turn into p.',
        'bdg-line': 'The line has one g and two d sounds. Say it with Peterson.',
      },
    },
    gh: {
      name: 'gh',
      title: 'A voiced throat sound',
      place:
        'The back of your tongue rises close to the soft palate or the uvula but does not touch it. The air rubs through the gap, and your vocal cords buzz.',
      start:
        'Start from the Finnish h in kahvi: it already rubs in the right place. Let your vocal cords buzz and you have gh. Another way: say g between two a sounds and loosen the closure until the air no longer stops.',
      test: 'You can hold gh, but not g. If the air stops for a moment, you said g.',
      error: 'A Finnish speaker easily says g or k, and the air stops with a small pop: «Ghīs» (the city) sounds like «gīs» ‘soul’. A plain h is too weak.',
      variants:
        'According to Peterson gh can be made at the soft palate or at the uvula. In his own recordings it is often partly voiceless, so a voiceless gh is close and not an error.',
      model: 'In Peterson’s recordings gh is almost always in forms of one word, «morghūlis», «morghon».',
      steps: {
        'gh-hear': 'Listen to gh in the middle of the word, slowly first, then at normal speed. The air rubs; it does not stop.',
        'gh-feel': 'Say kahvi and hold the h. Then let your vocal cords buzz.',
        'gh-sustain': 'Hold gh for three seconds. The air flows the whole time.',
        'gh-between-vowels': 'Say gh between each pair of vowels, with no closure.',
        'gh-word': 'gh sits between vowels. The air flows the whole time.',
        'gh-word-2': 'After r, gh easily turns into g. Keep the gap open.',
        'gh-line': 'Say the line with Peterson, slowly first.',
      },
    },
    q: {
      name: 'q',
      title: 'A k much further back',
      place: 'The back of your tongue closes off the air against the uvula, clearly further back than for k. The vowels next to it sound darker.',
      start: 'Say a Finnish k as in kuu and move the closure back until your tongue touches the uvula.',
      test: 'Say ka and qa in turn. In q you feel the touch deeper, and the a sounds darker.',
      error: 'You hear the difference in «qēlītsos» ‘candle’ and «kēlītsos» ‘kitten’. Peterson himself mostly says q as k, so k is close and never an error.',
      variants: 'k counts: Peterson says q as k in his own recordings.',
      model: 'In Peterson’s recordings q sounds like k. Listen to the rest of the word in them, and see where q is made in the picture.',
      steps: {
        'q-hear': 'Listen to the rhythm and the other sounds of the word. Peterson says the q as k here.',
        'q-feel': 'Say k and move the closure back to the uvula.',
        'q-sustain': 'You cannot hold a q, so repeat the syllable. The closure stays just as far back every time.',
        'q-between-vowels': 'Say q between each pair of vowels. Next to i and e the q drifts forward: keep it back.',
        'q-initial': 'The word starts with a closure against the uvula.',
        'q-word': 'The q starts the word, before i. Keep the closure back.',
        'q-line': 'Say the line with Peterson. He says the q as k; you say it from the back.',
      },
    },
    rh: {
      name: 'rh',
      title: 'A voiceless r',
      place: 'The tip of your tongue trills against the gum ridge as for r, but your vocal cords do not buzz. Your voice starts only in the vowel.',
      start: 'Whisper a Finnish r and say a vowel after it in full voice.',
      test: 'Keep your fingers on your throat: nothing buzzes during rh, the vowel buzzes. With your palm in front of your mouth you feel the air.',
      error: 'A voiced r is the usual rh error: «zȳrho» ‘his, her’ then sounds like «zȳro» ‘of the spouse’.',
      model: 'Peterson’s recordings have rh in only a few words. The picture shows the sound, and the recording gives you the rhythm of the word.',
      steps: {
        'rh-hear': 'Listen to the start of the word, slowly first, then at normal speed. rh is a trill without voice, and the voice starts in the vowel.',
        'rh-feel': 'Whisper r and say the vowel after it aloud.',
        'rh-sustain': 'Hold a voiceless trill for three seconds.',
        'rh-between-vowels': 'Say rh between each pair of vowels: the vowel sounds, the rh does not.',
        'rh-initial': 'The word starts with a voiceless trill.',
        'rh-line': 'Say the line with Peterson. The rh is right at the start.',
      },
    },
    lj: {
      name: 'lj',
      title: 'l and y at once',
      place:
        'The middle of your tongue presses against the hard palate, and the air flows past the sides of the tongue. The tip of your tongue stays down behind the lower teeth.',
      start: 'Say kalja and merge the l and the j into one sound: keep the tip of your tongue down and press the middle against the hard palate.',
      test: 'In a mirror you see the tip of your tongue stay down. You can hold the sound.',
      error: 'A Finnish speaker easily says l first and then j. Then you hear two movements instead of one.',
      model: 'Peterson’s recordings have lj in only two words, both in the middle of a line. The picture shows the sound.',
      steps: {
        'lj-hear': 'Listen: lj is one sound.',
        'lj-feel': 'Say kalja and merge the l and the j.',
        'lj-sustain': 'Hold lj for three seconds with the tip down.',
        'lj-between-vowels': 'Say lj between each pair of vowels as one sound.',
        'lj-word': 'lj comes after the long ū in one movement.',
        'lj-line': 'Say the line with Peterson, slowly first. The lj is in «morghūljosy».',
      },
    },
    ny: {
      name: 'ñ',
      title: 'n and y at once',
      place:
        'The middle of your tongue closes off the air against the hard palate, and the air flows out through the nose. The tip of your tongue stays down behind the lower teeth.',
      start: 'Say Sonja and merge the n and the j into one sound.',
      test: 'Pinch your nostrils shut: ñ stops, because the air flows through the nose. In a mirror you see the tip of your tongue stay down.',
      error: 'A Finnish speaker easily says n followed by j, or just n. Peterson’s ñ is long and even.',
      steps: {
        'ny-hear': 'Listen to the start of the word: one sound, not n and j.',
        'ny-feel': 'Say Sonja and merge the n and the j.',
        'ny-sustain': 'Hold ñ for three seconds with the tip down.',
        'ny-between-vowels': 'Say ñ between each pair of vowels as one sound.',
        'ny-initial': 'The word starts with ñ. Do not start with n.',
        'ny-word': 'The ñ sits between vowels as one sound, not n and j.',
        'ny-line': 'Say the line with Peterson. The ñ is in «ōñoso».',
      },
    },
    y: {
      name: 'y',
      title: 'The Finnish y',
      place: 'Your tongue is high and front as for i, but your lips are rounded as for u.',
      start: 'You already have the right y. Say a long i and round your lips without moving your tongue: i turns into y.',
      test: 'Look in a mirror: in y your lips are round and pushed forward, in i they are spread.',
      error:
        'Many of the shows’ actors say y as i, and Peterson’s y often sounds like i in his recordings too. Say your own Finnish y anyway. You hear the difference in «pykagon» ‘to spit’ and «pikagon» ‘to follow’.',
      variants: 'According to Peterson today’s speakers often say y without rounding the lips, so i is close and not an error. The aim is still y.',
      model: 'Peterson’s y and i sound almost the same in his recordings. Your own Finnish y is the model.',
      steps: {
        'y-hear': 'Listen to the word from Peterson, and say the y yourself with rounded lips.',
        'y-feel': 'Say i and round your lips. Your tongue stays put.',
        'y-sustain': 'Hold y for three seconds with your lips rounded.',
        'y-pair': 'Say the syllable pairs in turn. Only your lips move.',
        'y-initial': 'Your lips are rounded from the very start.',
        'y-final': 'The word ends in y. Do not let your lips spread at the end.',
        'y-word': 'The y is in the last syllable. Keep your lips rounded.',
        'y-line': 'The line has three y sounds. Say it with Peterson, with your own y.',
      },
    },
    r: {
      name: 'r',
      title: 'A trill as in Finnish',
      place: 'The tip of your tongue trills against the gum ridge two or three times, and your vocal cords buzz.',
      start: 'Your Finnish r is already right. Keep it a trill in High Valyrian words too, and do not shorten it to a single tap.',
      test: 'Hold r: the trill goes on as long as your breath lasts.',
      error:
        'The shows’ actors often say r as a single tap. According to the wiki that counts too, especially after a stop, so a tap is close and not an error.',
      variants: 'A single tap counts, especially after a stop (the wiki).',
      steps: {
        'r-hear': 'Listen to r between vowels.',
        'r-feel': 'Say a long Finnish r and feel the tip of your tongue trill.',
        'r-between-vowels': 'Say r between each pair of vowels. Two or three taps are enough.',
        'r-initial': 'The word starts with r: trill from the very start.',
        'r-final': 'The word ends in r. Let it trill to the end.',
        'r-word': 'The r sits between vowels. Even a short trill is enough.',
        'r-line': 'Say the line with Peterson. Both words have an r between vowels.',
      },
    },
    voice: {
      name: 'Stress and pitch',
      title: 'Where your voice rises',
      place:
        'In High Valyrian stress is counted from the end of the word, and your voice rises on the stressed syllable. In Finnish the stress is always on the first syllable.',
      start: 'Tap the table on every syllable, harder on the stressed one. Let your voice rise in the same place.',
      test: 'Watch the pitch line: it should rise on the stressed syllable, not at the start of the word.',
      error: 'A Finnish speaker stresses the first syllable: KI·rim·vo·se. The right one is ki·RIM·vo·se. In a command the stress is on the last syllable.',
      model: 'The pitch line is a measurement, not a grade. It shows where your voice rises but does not decide whether the stress was on the right syllable.',
      steps: {
        'voice-hear': 'Listen to the voice rising on the second syllable.',
        'voice-feel': 'Tap the syllables on the table, the stressed one harder.',
        'voice-sustain': 'Say a long a and let your voice rise and fall. This shows how wide your range is.',
        'voice-initial': 'In a two-syllable word the stress is at the start, as in Finnish.',
        'voice-final': 'The word is a command, so the stress is on the last syllable.',
        'voice-word': 'The stress is on the middle syllable.',
        'voice-word-2': 'The stress is on the first syllable, because the second-to-last syllable is light and the one before it heavy.',
        'voice-line': 'Say the line with Peterson. Your voice rises on the stressed syllable of every word.',
      },
    },
  },
};

// Messages.coachGuide (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('coachGuide', coachGuideFi, coachGuideEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof coachGuideFi> {}
}
